import type {
  ConfigurationModuleResourceReadBffV2Api,
  ParameterSetFieldValueItemStructureCellBffV2Api,
  ParameterSetFieldValueItemStructureRowsBffV2Api,
  ParameterSetFieldValueStructureBffV2Api,
  ParameterSetValueItemBffV2Api,
} from '@ingenico/api-sdk-device-management-private-v2';
import type {
  ConfigurationModuleStructureParameterValue,
  ConfigurationModuleStructureParameterValueCell,
} from '@ingenico/react-smartfield-utils';
import { toString } from 'lodash-es';
import type {
  ConfigurationModule,
  ConfigurationModuleParameterSetValue,
  ConfigurationModuleResourceBff,
} from '../../models/configurationModules.model';
import { toIsoStringIfDate } from '../utils/date.utils';

export function mapConfigurationByIdBffResponse(
  response: ConfigurationModuleResourceReadBffV2Api,
): ConfigurationModuleResourceBff {
  return {
    creationDate: toIsoStringIfDate(response.creationDate),
    createdBy: response.createdBy,
    updatedBy: response.updatedBy,
    updatedDate: toIsoStringIfDate(response.updatedDate),
    description: response.description,
    name: response.name,
    id: response.id,
    parameters: {
      parameterSets: response.parameterSets.map(({ values, templateId }) => ({
        values: mapParameterSetValues(values),
        template: { id: templateId },
      })),
    },
    softwareVersion: {
      softwareName: response.softwareVersion.softwareName,
      softwareVersionNumber: response.softwareVersion.softwareVersionNumber,
      type: response.softwareVersion.type,
      id: response.softwareVersion.id,
      softwareId: response.softwareVersion.softwareId,
    },
  };
}

export function mapConfigurationByIdResponse(response: ConfigurationModuleResourceReadBffV2Api): ConfigurationModule {
  return {
    creationDate: toIsoStringIfDate(response.creationDate),
    createdBy: response.createdBy,
    updatedBy: response.updatedBy,
    updatedDate: toIsoStringIfDate(response.updatedDate),
    description: response.description,
    name: response.name,
    id: response.id,
    parameterSets: response.parameterSets.map(({ values, templateId, id }) => ({
      values: mapParameterSetValues(values),
      id,
      templateId,
    })),
    softwareVersion: response.softwareVersion,
  };
}

export function mapParameterSetValues(values: ParameterSetValueItemBffV2Api[]): ConfigurationModuleParameterSetValue[] {
  return values.map((value) => ({
    fieldId: value.fieldId,
    fieldValue: {
      type: value.fieldValue.smartField ? 'SMART_FIELD' : (value.fieldValue.type ?? 'SINGLE'),
      single: value.fieldValue.single,
      structure: mapStructure(value.fieldValue.structure),
      ...(value.fieldValue.smartField?.id
        ? {
            smartField: {
              id: value.fieldValue.smartField.id,
              name: value.fieldValue.smartField.name,
            },
          }
        : {}),
    },
  }));
}

function mapStructure(
  structures?: ParameterSetFieldValueStructureBffV2Api,
): ConfigurationModuleStructureParameterValue[] | undefined {
  if (structures === undefined) {
    return undefined;
  }

  return structures.map((structure: ParameterSetFieldValueItemStructureRowsBffV2Api) => {
    return {
      rowId: toString(structure.rowId),
      cells: structure.cells.map((cell: ParameterSetFieldValueItemStructureCellBffV2Api) => {
        return mapCell(cell);
      }),
    };
  });
}

function mapCell(
  cell: ParameterSetFieldValueItemStructureCellBffV2Api,
): ConfigurationModuleStructureParameterValueCell {
  if (cell?.fieldValue && cell.fieldValue.type === 'STRUCTURE' && cell.fieldValue.structure) {
    return {
      columnId: cell.columnId,
      fieldValue: {
        ...cell.fieldValue,
        type: toString(cell.fieldValue.type),
        structure: mapStructure(cell.fieldValue.structure),
        ...(cell.fieldValue.smartField
          ? {
              smartField: {
                id: toString(cell.fieldValue.smartField.id),
                name: toString(cell.fieldValue.smartField.name),
              },
            }
          : {}),
      },
    } as ConfigurationModuleStructureParameterValueCell;
  }
  return {
    columnId: cell.columnId,
    fieldValue: {
      ...cell.fieldValue,
      type: toString(cell.fieldValue.type),
      ...(cell.fieldValue.smartField
        ? {
            smartField: {
              id: toString(cell.fieldValue.smartField.id),
              name: toString(cell.fieldValue.smartField.name),
            },
          }
        : {}),
    },
  } as ConfigurationModuleStructureParameterValueCell;
}
